import { useCallback, useEffect, useState } from "react";

import { createQuoteScheduler } from "@/modules/dashboard/crypto-swaps/utils/cryptoSwapQuoteScheduler";
import { HttpApiService } from "@/shared/services/httpApi.service";
import type { LimitPayerType } from "@/shared/services/payments/paymentCorridors.service";

import {
  type PayinRail,
  PaymentsPayinService,
  type PreviewPayinPayload,
  type PreviewPayinResponse,
  readPayinErrorCode,
} from "../services/paymentsPayin.service";

/** Wait for the user to stop typing before asking for a price (same cadence as the swap quote). */
const PREVIEW_DEBOUNCE_DELAY = 500;

export interface PayinFeePreviewRequest {
  organizationId: string;
  rail: PayinRail;
  country: string;
  currency: string;
  /** Which amount the user last edited — it becomes the fixed side of the preview. */
  field: "gross" | "net";
  /** Raw amount as typed (decimal string, no separators). */
  amount: string;
  /** Payer kind when the host already knows it — the preview then checks that kind's limits, not the envelope. */
  payerType?: LimitPayerType | null;
}

export interface UsePayinFeePreviewResult {
  preview: PreviewPayinResponse | null;
  loading: boolean;
  error: string | null;
  /** Machine-readable detail when the backend sent one (`amount_above_maximum`, …); null otherwise. */
  errorCode: string | null;
  /** Debounced: schedules a preview for the last edit; invalid/empty amounts clear the current one. */
  request: (input: PayinFeePreviewRequest) => void;
  /** Drop the current preview (e.g. when the form resets). */
  reset: () => void;
}

/**
 * Live payin fee preview (`POST /internal/payments/payins/preview`), debounced like the swap quote: the field
 * the user last edited drives, the response fills the derived side.
 *
 * Last-wins, from the keystroke: every edit (and `reset`) invalidates whatever is pending or in flight AT
 * ONCE, not when its own debounce fires. Invalidating only when the next request left let an older answer
 * land during those 500 ms, clear `loading` and publish the price of an amount the user had already changed —
 * 50.000 typed, 10.000 priced, and the host's confirmation enabled.
 */
export function usePayinFeePreview(): UsePayinFeePreviewResult {
  const [preview, setPreview] = useState<PreviewPayinResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const [scheduler] = useState(() =>
    createQuoteScheduler<PreviewPayinPayload, PreviewPayinResponse>({
      delayMs: PREVIEW_DEBOUNCE_DELAY,
      run: (payload, signal) => PaymentsPayinService.previewPayin(payload, signal).then((res) => res.data),
      onResult: (result) => {
        setPreview(result);
        setLoading(false);
      },
      onError: (err) => {
        setPreview(null);
        setError(HttpApiService.getErrorMessage(err));
        setErrorCode(readPayinErrorCode(err));
        setLoading(false);
      },
    }),
  );

  useEffect(() => () => scheduler.cancel(), [scheduler]);

  const reset = useCallback(() => {
    scheduler.cancel();
    setPreview(null);
    setLoading(false);
    setError(null);
    setErrorCode(null);
  }, [scheduler]);

  const request = useCallback(
    (input: PayinFeePreviewRequest) => {
      // An empty or non-positive amount is not an error — it just means "nothing to price yet".
      if (!/^\d+(\.\d+)?$/.test(input.amount) || Number(input.amount) <= 0) {
        reset();
        return;
      }

      setLoading(true);
      setError(null);
      setErrorCode(null);
      scheduler.schedule(
        {
          organization_id: input.organizationId,
          rail: input.rail,
          country: input.country,
          currency: input.currency,
          ...(input.field === "gross" ? { gross_amount: input.amount } : { net_amount: input.amount }),
          ...(input.payerType ? { payer_type: input.payerType } : {}),
        },
        false,
      );
    },
    [reset, scheduler],
  );

  return { preview, loading, error, errorCode, request, reset };
}
